// Treasurer-first copy for the calendar period-lock (ADR-019) and externally-filed
// (ADR-018) surfaces. These are frontend-only strings — they have NO C# mirror, so
// they live here rather than in the contract-tested `PreflightCopy` bundle (which is
// pinned to `Features.Finance.Filing.Preflight.PreflightCopy`). No engineer enum identifier appears
// in any of them (Finance CLAUDE.md → translate at the render boundary).
export const FilingLockCopy = {
  // ADR-019 — a locked period: its report is filed and the deadline has passed (or the
  // period sits below the onboarding backfill floor), so corrections require an amendment.
  LockedBadge: 'Locked',
  LockedBadgeTooltip:
    'This reporting period is closed because its report has been filed. A correction is an amendment.',
  // An amendment still being prepared whose period was re-locked (by hand): the treasurer is
  // already amending, so the way back in is the reopen alone.
  LockedAmendmentBanner:
    'This reporting period is locked. Reopen the period to keep working on this amendment.',

  // ADR-019 D4 — a manually-reopened locked period.
  ReopenedBadge: 'Reopened',
  ReopenedBanner:
    'This period is reopened. It will re-lock automatically the next time you file it.',

  // ADR-018 item 3 — an externally-filed (onboarding-backfill) report. This label
  // renders on the `filingOrigin === ExternalPortal` axis (NOT a state gate), so the
  // compact chip stays jurisdiction-neutral; the tooltip names the campaign's own
  // filing authority via `externalLabelTooltip(stateDisplayName)`.
  ExternalLabel: 'Filed externally · imported at onboarding',

  // ADR-019 §"Edge cases" — an open (still-editable) period that already has a
  // generated/filed report. Editing is intentionally still free (regenerate is
  // free before the deadline), but warn so a treasurer who thinks "I already filed
  // this" isn't surprised. A nudge, not a block.
  OpenAlreadyFiledNudge:
    "Already filed — edits here won't change what's on file until you re-file (amend instead).",

  // ADR-018 item 2 — the non-itemized remainder (AL form lines 2b/5b), rendered as an
  // explicit line so itemized rows + this line + the frozen totals foot.
  ItemizedContributionsLabel: 'Itemized contributions',
  ItemizedExpendituresLabel: 'Itemized expenditures',
  NonItemizedContributionsLabel: 'Non-itemized contributions',
  NonItemizedExpendituresLabel: 'Non-itemized expenditures',
  NonItemizedHelpLine:
    'Small contributions/expenditures below the itemization threshold, reported as a single total on your filed report.',
  // ADR-018 item 2 — the backend computes the remainder UNCLAMPED, so a negative value
  // means the itemized rows exceed the frozen total: a backfill data error. Surface it
  // (alert-don't-block) so the treasurer re-checks the imported figures, rather than
  // silently hiding the line.
  NonItemizedNegativeWarning:
    'Non-itemized total is negative — re-check the imported figures.',

  // ADR-019 D4 — the easy, one-action, audited reopen affordance.
  ReopenButton: 'Reopen this period',
  RelockButton: 'Re-lock this period',
  ReopenReasonLabel: 'Reason (required)',
  ReopenReasonPlaceholder: 'Why are you reopening this period?',
  ReopenSubmit: 'Reopen period',
  RelockSubmit: 'Re-lock period',
  ReopenCancel: 'Cancel',
  // Context-aware messaging (ADR-019 D4): a never-filed period reopens clean; an
  // already-filed / externally-filed one reopens as an amendment.
  ReopenNeverFiledMessage:
    "This period's filing deadline has passed. Reopen it to file it now — nothing is on file yet.",
  // "Yes, I filed it with the state" where the app rebuilds the amendment (ADR-021
  // SelfContainedUpload / ElectronicResubmit).
  StartAmendmentMessage:
    "Because it's on file with the state, a change is an amendment. Starting one opens this period so you can correct its transactions, and it locks again when you file the amendment.",
  StartAmendmentButton: 'Start the amendment',
  ReopenAmendmentMessage:
    "This period is already filed. Reopening it means a correction is an amendment — re-uploading won't change rows already on file.",

  // ADR-018 Neutral / S3 — bounded onboarding caveat surfaced after a backfill.
  ImportedHistoryCaveatHeading: 'About your imported history',
  ImportedHistoryCaveat:
    "Itemized history is exact; a small amount of non-itemized contributions could not be attributed per-donor and may slightly undercount a recurring small donor's running total.",
} as const;

// A superseded (Amended) report is not the filing in force, so its page sends the treasurer to the
// amendment that replaced it. `headAmendmentNumber` is null until the filing chain loads.
export function supersededNotice(headAmendmentNumber: number | null): string {
  const replacedBy = headAmendmentNumber === null ? 'An amendment' : `Amendment #${headAmendmentNumber}`;
  return `${replacedBy} replaced this filing and is the version on record. Make any correction there.`;
}

// A filed report whose amendment is still being prepared shares that amendment's (reopened) period.
export function amendmentInProgressNotice(amendmentNumber: number): string {
  return `Amendment #${amendmentNumber} is in progress. Make this period's changes there.`;
}

export function continueAmendmentMessage(amendmentNumber: number): string {
  return `Amendment #${amendmentNumber} is already open for this period. Continue it to make your changes. The period locks again when you file it.`;
}

// ADR-018 item 3 — tooltip for an externally-filed (onboarding-backfill) report.
// Renders on the `filingOrigin === ExternalPortal` axis, not a state gate, so the
// jurisdiction name is passed in (the campaign's resolved `stateDisplayName`) rather
// than hardcoded "Alabama". When the display name is null (federal / unresolved) the
// tooltip stays jurisdiction-neutral and names no filing authority.
export function externalLabelTooltip(stateDisplayName: string | null): string {
  return (
    `You filed this report directly${
      stateDisplayName ? ` with the ${stateDisplayName} filing authority` : ''
    } and we imported it when you onboarded. There is no in-system package to download or re-file.`
  );
}
